import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { KEYS } from '../../util/event-keys';
import { announce } from '../../util/LiveAnnouncer';
import { BlocksElement } from '../BlocksElement';
import { BlocksTableRow } from '../table-row/table-row';
import styles from './table-body.styles';

/**
 * @slot default - The table body content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksTableBody extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Delay for announcement like "please enter space to drop"
   */
  @property({ type: Number })
  dropAnnouncementDelay = 3000;

  /**
   * Text to announce when a row is dragged
   */
  @property({ type: String })
  rowDraggedText = 'Row dragged';

  /**
   * Text to announce when a row is dropped
   */
  @property({ type: String })
  rowDroppedText = 'Row dropped';

  /**
   * Text template to announce when a row is to be inserted between two specified rows.
   * Specify a template such as "Insert between row {prevRow} and {nextRow}". The placeholders will be
   * replaced with actual row numbers during screen reader announcements.
   */
  @property({ type: String })
  insertBetweenText = 'Insert between row {prevRow} and {nextRow}';

  /**
   * Text template to announce when a row is to be inserted after first row.
   * Specify a template such as "Insert before row {nextRow}". The placeholders will be
   * replaced with actual row numbers during screen reader announcements.
   */
  @property({ type: String })
  insertBeforeText = 'Insert before row {nextRow}';

  /**
   * Text template to announce when a row is to be inserted before last row.
   * Specify a template such as "Insert after row {prevRow}". The placeholders will be
   * replaced with actual row numbers during screen reader announcements.
   */
  @property({ type: String })
  insertAfterText = 'Insert after row {prevRow}';

  /**
   * Text to announce prompting the user to press space or enter to drop the row
   */
  @property({ type: String })
  clickToDropText = 'press enter/space to drop the row';

  /**
   * Query all the rows in the cat table
   */
  private get _catTableRows(): Array<BlocksTableRow> {
    return Array.from(this.children) as Array<BlocksTableRow>;
  }
  private isDraggingInProgress = false;
  private arrowDirection: string | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private dragRow: BlocksTableRow | null = null;

  constructor() {
    super();
    this.addEventListener('bl-keyboard-drag-start', this.handleDragStart);
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleDragReset);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleDragReset);
  }

  firstUpdated() {
    // announce earlier to create aria live region placeholder
    announce('');
    this.updateRowPositions();
  }

  private readonly handleDragStart = (e: CustomEvent) => {
    const { dragRow } = e.detail;

    const children = dragRow?.closest('cat-table-body')?.querySelectorAll('cat-table-row');

    let drawRowSiblings: BlocksTableRow[] = [];

    if (children) {
      drawRowSiblings = (Array.from(children) as Array<BlocksTableRow>).filter(
        (row: BlocksTableRow) =>
          row.isDraggable === true && row !== dragRow
      );
    }

    if (drawRowSiblings?.length === 0) {
      return false;
    }

    const parentElement = dragRow.shadowRoot.querySelector('.cat-c-table-row') as HTMLElement;

    parentElement.setAttribute('tabindex', '-1')
    parentElement.focus();

    this.isDraggingInProgress = true;
    this.dragRow = dragRow as BlocksTableRow;

    // announces row dragged text
    this.dragRow.dragAnnouncementText = this.rowDraggedText;

    // announces in between rows as well after a slight delay
    setTimeout(() => {
      this.announce(dragRow);
    }, 300);
  };

  private readonly handleDragEnd = async (e: KeyboardEvent) => {
    e.preventDefault();
    const target = e.target as BlocksTableRow;
    // since start and end are same element
    if (target.tagName === 'CAT-TABLE-CELL') {
      return;
    }

    this.dispatch({
      eventName: 'bl-keyboard-drag-end',
      detailObj: { row: target, direction: this.arrowDirection }
    });
    this.timeoutId = null;
    this.isDraggingInProgress = false;
    this.arrowDirection = null;
    announce(this.rowDroppedText);
    await this.updateComplete;
    setTimeout(() => {
      const rowElement = target?.shadowRoot?.querySelector('.cat-c-table-row') as HTMLElement;
      const rowNumber = Number(rowElement.getAttribute('data-row'));
      // In angular tab index is getting reset on table body rerendering. Hence picking row with index value.
      const targetRow = this._catTableRows[rowNumber - 1];
      const targetElement = targetRow?.shadowRoot?.querySelector('.cat-c-table-row') as HTMLElement;
      targetElement?.setAttribute('tabindex', '-1');
      targetElement?.focus();
    }, 100);
  };

  private readonly handleDragReset = () => {
    // if focus lost during the drag process then reset required
    if (this.isDraggingInProgress) {
      this.dispatch({
        eventName: 'bl-keyboard-drag-reset'
      });
    }
  };

  private readonly getDeepActiveElement = () => {
    let activeElement = document.activeElement;
    while (activeElement?.shadowRoot) {
      activeElement = activeElement.shadowRoot.activeElement || activeElement;
    }
    return activeElement;
  };

  private readonly makeAnnouncement = (e: KeyboardEvent) => {
    e.preventDefault();
    let currentActiveRow: BlocksTableRow | null = null;
    let currentIndex: number;
    const currentActiveElement = this.getDeepActiveElement();
    const currentActiveRowNode = currentActiveElement?.getRootNode();
    if (currentActiveRowNode instanceof ShadowRoot) {
      currentActiveRow = currentActiveRowNode.host as BlocksTableRow;
    }
    const children = Array.from(this.children) as BlocksTableRow[];
    if (currentActiveRow) {
      currentIndex = children.indexOf(currentActiveRow);
      if (e.key === KEYS.DOWN_ARROW && currentIndex < children.length - 1) {
        this.arrowDirection = 'after';
        const row = children[currentIndex + 1];
        this.setFocusAndAnnounce(row);
      } else if (e.key === KEYS.UP_ARROW && currentIndex > 0) {
        this.arrowDirection = 'before';
        const row = children[currentIndex - 1];
        this.setFocusAndAnnounce(row);
      }
    }
  };

  private handleOnEscape = () => {
    const currentActiveElement = document?.activeElement?.shadowRoot?.activeElement as HTMLElement;
    const firstTablecell = currentActiveElement.children[0];
    const btn = firstTablecell.shadowRoot?.querySelector('.cat-c-table-cell__draggable-button') as HTMLElement;
    btn?.focus();
    this.dispatch({
      eventName: 'bl-keyboard-drag-reset'
    });
    this.isDraggingInProgress = false;
  };

  private readonly handleKeyDown = (e: KeyboardEvent) => {
    if (this.isDraggingInProgress && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
      this.handleDragEnd(e);
    }

    if (this.isDraggingInProgress) {
      this.makeAnnouncement(e);
    }

    if (this.isDraggingInProgress && e.key === KEYS.ESCAPE) {
      this.handleOnEscape();
    }
  };

  private getRowNumber = (row: BlocksTableRow) => {
    if (row) {
      const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row') as HTMLElement;
      const rowNumberByIndex = this._catTableRows.indexOf(row) + 1;
      return rowElement.getAttribute('data-row') ?? rowNumberByIndex;
    }
    return '';
  };

  private setFocusAndAnnounce = (row: BlocksTableRow) => {
    const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row') as HTMLElement;
    this.announce(row);
    rowElement.setAttribute('tabindex', '-1');
    setTimeout(() => {
      rowElement?.focus();
    }, 0);
  };

  private updateRowPositions = () => {
    const rows = Array.from(this.children);
    rows.forEach((row, index) => {
      const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row') as HTMLElement;
      if (rowElement) {
        rowElement.setAttribute('data-row', `${index + 1}`);
      }
    });
  };

  private announce = (row: BlocksTableRow, text = '') => {
    let announcementText = text;
    if (this._catTableRows) {
      const firsRow = this._catTableRows[0];
      const lastRow = this._catTableRows[this._catTableRows.length - 1];
      const prevRow = row.previousElementSibling as BlocksTableRow;
      const nextRow = row.nextElementSibling as BlocksTableRow;
      const prevRowNumber = this.getRowNumber(prevRow);
      const nextRowNumber = this.getRowNumber(nextRow);
      if (row.isEqualNode(firsRow)) {
        announcementText = this.insertBeforeText.replace('{nextRow}', nextRowNumber.toString());
      } else if (row.isEqualNode(lastRow)) {
        announcementText = this.insertAfterText.replace('{prevRow}', prevRowNumber.toString());
      } else {
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
        }
        announcementText = this.insertBetweenText.replace('{prevRow}', prevRowNumber.toString()).replace('{nextRow}', nextRowNumber.toString());
        // announces after delay
        this.timeoutId = setTimeout(() => {
          if (this.isDraggingInProgress) {
            row.dragAnnouncementText = this.clickToDropText;
          }
        }, this.dropAnnouncementDelay);
      }
    }
    row.dragAnnouncementText = announcementText;
  };

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table-body', {});

    return html`
      <div part="base" role="rowgroup" class="${componentClassName}" @keydown="${this.handleKeyDown}">
        <slot
          @slotchange=${() => {
        this.updateRowPositions();
      }}
          part="part-default"
        ></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-table-body') === undefined) {
  customElements.define('cat-table-body', BlocksTableBody);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table-body': BlocksTableBody;
  }
}
